import type {
  BusinessCenterCompanyRecord,
  BusinessCenterOnboardingRecord,
  BusinessCenterRoleKey,
  BusinessCenterUserRecord,
  BusinessCenterWorkspaceAssignmentRecord,
  BusinessCenterWorkspaceAuditRecord,
  BusinessCenterWorkspaceFeatureFlag,
  BusinessCenterWorkspaceKey,
  BusinessCenterWorkspaceNavigationItem,
  BusinessCenterWorkspacePermissionProfileRecord,
  BusinessCenterWorkspaceProvisioningStatus,
  BusinessCenterWorkspaceRecord,
  BusinessCenterWorkspaceStatus,
  BusinessCenterWorkspaceStatusHistoryRecord,
  BusinessCenterWorkspaceTemplateRecord,
  BusinessCenterWorkspaceType,
} from "../../lib/types";
import { resolveBusinessCenterBrandingProfile } from "./businessCenterBrandingData";
import { loadBusinessCenterCompanies, loadBusinessCenterCompanyUsers, mapCompanyRoleToPermissionRole } from "./businessCenterCompanyUsersData";
import { loadBusinessCenterOnboardingRecords } from "./businessCenterOnboardingData";
import { businessCenterPermissionMatrixDefaults } from "./businessCenterRolesData";
import {
  bulkImportBusinessCenterWorkspaces,
  getBusinessCenterWorkspaces,
  updateBusinessCenterWorkspace,
  upsertBusinessCenterWorkspace,
} from "../../lib/api";

let workspaceProvisioningCache: BusinessCenterWorkspaceRecord[] | null = null;

export const workspaceStatusOptions: BusinessCenterWorkspaceStatus[] = ["Draft", "Provisioning", "Active", "Paused", "Archived"];

export const workspaceProvisioningStatusOptions: BusinessCenterWorkspaceProvisioningStatus[] = ["Not Started", "In Progress", "Pending", "Complete"];

export const workspaceTemplateCatalog: BusinessCenterWorkspaceTemplateRecord[] = [
  buildWorkspaceTemplate({
    key: "custom",
    label: "Business Center Core",
    description: "The shared company and administration workspace for multi-tenant governance.",
    companyType: "parent",
    workspaceType: "core",
    brand: "business_center",
    primaryColor: "#1c69d4",
    accentColor: "#14b8a6",
    defaultModules: ["Dashboard", "Company Management", "User Management", "Roles & Permissions", "Onboarding", "Branding", "Automation", "Reporting"],
    defaultNavigation: [
      navItem("Dashboard", "/admin/truevance-business-center", "Command center overview and governance surface.", "Core", 0),
      navItem("Companies", "/admin/truevance-business-center#companies", "Parent and child company visibility.", "Core", 1),
      navItem("Users", "/admin/universal-user-management", "Company-scoped user provisioning and role mapping.", "Governance", 0),
      navItem("Onboarding", "/admin/client-onboarding-center", "Shared onboarding workflow and workspace assignment.", "Governance", 1),
      navItem("Branding", "/admin/white-label-branding-center", "Portal, document, and email branding.", "Governance", 2),
      navItem("Automation", "/admin/automation-engine", "Reusable workflow and notification automation.", "Governance", 3),
      navItem("Reporting", "/admin/truevance-analytics-center", "Company and workspace reporting.", "Insights", 0),
    ],
    defaultFeatureFlags: [
      featureFlag("multi_tenant", "Multi-tenant governance", "Company-scoped platform controls across every workspace.", true),
      featureFlag("user_management", "User management", "Provision users and role assignments across companies.", true),
      featureFlag("workspace_provisioning", "Workspace provisioning", "Create, activate, and monitor shared workspace shells.", true),
      featureFlag("white_label", "White-label support", "Branding, domains, and portal presentation controls.", true),
      featureFlag("automation", "Automation ready", "Workflow hooks available for later integrations.", true),
    ],
    defaultRoleKeys: ["admin", "manager", "team_member", "client", "viewer"],
  }),
  buildWorkspaceTemplate({
    key: "kreatethedate",
    label: "Kreatethedate Workspace",
    description: "RSVP, memory, archive, media vault, agreements, invoices, and projects.",
    companyType: "client_company",
    workspaceType: "vertical",
    brand: "kreatethedate",
    primaryColor: "#ff6b35",
    accentColor: "#14b8a6",
    defaultModules: ["RSVP Hub", "Memory Hub", "Archive Hub", "Memory Media Vault", "Agreements", "Invoices", "Projects"],
    defaultNavigation: [
      navItem("RSVP Hub", "/rsvp-hub", "Client-facing RSVP workflow.", "Client Experience", 0),
      navItem("Memory Hub", "/memory-hub", "Memory planning and archive entry points.", "Client Experience", 1),
      navItem("Archive Hub", "/archive-hub", "Legacy content archive management.", "Client Experience", 2),
      navItem("Memory Media Vault", "/memory-media-vault", "Media collection and preservation workspace.", "Client Experience", 3),
      navItem("Agreements", "/admin/agreement-records", "Agreement tracking and signature history.", "Operations", 0),
      navItem("Invoices", "/admin/payment-records", "Payment and invoice oversight.", "Operations", 1),
      navItem("Projects", "/admin/truevance-project-center", "Delivery and milestone tracking.", "Operations", 2),
    ],
    defaultFeatureFlags: [
      featureFlag("portal", "Client portal", "Workspace-scoped portal access and handoff flows.", true),
      featureFlag("media_vault", "Memory media vault", "Media collection, review, approval, delivery, and archive.", true),
      featureFlag("agreements", "Agreement workflows", "Proposal, agreement, and payment lifecycle support.", true),
      featureFlag("projects", "Projects", "Project delivery and status tracking.", true),
    ],
    defaultRoleKeys: ["admin", "manager", "team_member", "client", "viewer"],
  }),
  buildWorkspaceTemplate({
    key: "truevance_connect",
    label: "Truevance Connect Workspace",
    description: "Assessments, recommendations, consulting projects, documents, and invoices.",
    companyType: "client_company",
    workspaceType: "vertical",
    brand: "truevance_connect",
    primaryColor: "#06b6d4",
    accentColor: "#22c55e",
    defaultModules: ["Sales & Lead Center", "Assessments", "Recommendations", "Projects", "Documents", "Client Portal", "Reports"],
    defaultNavigation: [
      navItem("Sales & Lead Center", "/admin/truevance-sales-center", "Shared CRM and discovery workflow.", "Sales", 0),
      navItem("Assessments", "/admin/truevance-assessment-center", "Business assessment and recommendation delivery.", "Delivery", 0),
      navItem("Recommendations", "/admin/truevance-recommendations", "Consulting recommendation records.", "Delivery", 1),
      navItem("Projects", "/admin/truevance-project-center", "Delivery projects and milestones.", "Delivery", 2),
      navItem("Documents", "/admin/truevance-document-center", "Shared document management.", "Operations", 0),
      navItem("Client Portal", "/client/portal", "Company-scoped portal access.", "Portal", 0),
      navItem("Reports", "/admin/truevance-analytics-center", "Commercial and delivery reporting.", "Insights", 0),
    ],
    defaultFeatureFlags: [
      featureFlag("crm", "CRM shared layer", "Lead, opportunity, and follow-up workflow access.", true),
      featureFlag("assessment", "Assessment center", "Structured assessments and recommendations.", true),
      featureFlag("delivery", "Project delivery", "Projects, tasks, and documents.", true),
      featureFlag("portal", "Client portal", "Scoped portal access for clients and viewers.", true),
    ],
    defaultRoleKeys: ["admin", "manager", "team_member", "client", "viewer"],
  }),
  buildWorkspaceTemplate({
    key: "memory_media_vault",
    label: "Memory Media Vault Workspace",
    description: "Collect, review, approve, deliver, archive, and preserve media collections.",
    companyType: "client_company",
    workspaceType: "vertical",
    brand: "memory_media_vault",
    primaryColor: "#0f172a",
    accentColor: "#f59e0b",
    defaultModules: ["Upload Center", "Review Queue", "Approval Center", "Gallery Center", "Archive Center", "Search Center", "Collections"],
    defaultNavigation: [
      navItem("Upload Center", "/client/memory-media-vault/upload", "Media intake and upload workflows.", "Media", 0),
      navItem("Review Queue", "/client/memory-media-vault/review", "Pending review and approval items.", "Media", 1),
      navItem("Approval Center", "/client/memory-media-vault/approval", "Approver queue and decision history.", "Governance", 0),
      navItem("Gallery Center", "/client/memory-media-vault/gallery", "Delivery and curation surface.", "Delivery", 0),
      navItem("Archive Center", "/client/memory-media-vault/archive", "Long-term preservation surface.", "Archive", 0),
      navItem("Search Center", "/client/memory-media-vault/search", "Search and metadata management.", "Archive", 1),
    ],
    defaultFeatureFlags: [
      featureFlag("upload", "Upload", "Direct upload and intake support.", true),
      featureFlag("approve", "Approval workflow", "Review and approve or reject submitted media.", true),
      featureFlag("archive", "Archive support", "Long-term preservation and searchability.", true),
      featureFlag("delivery", "Delivery tools", "Gallery and client delivery surfaces.", true),
    ],
    defaultRoleKeys: ["admin", "manager", "team_member", "client", "viewer"],
  }),
  buildWorkspaceTemplate({
    key: "ar_recovery",
    label: "AR Recovery Workspace",
    description: "Recovery dashboard, customer accounts, ticklers, promises, and reporting.",
    companyType: "client_company",
    workspaceType: "vertical",
    brand: "ar_recovery",
    primaryColor: "#7c3aed",
    accentColor: "#ec4899",
    defaultModules: ["Recovery Dashboard", "Customer Accounts", "Aging Management", "Promise-to-Pay", "Collection Letters", "Statements", "Reporting"],
    defaultNavigation: [
      navItem("Recovery Dashboard", "/admin/ar-recovery", "Command center overview.", "Recovery", 0),
      navItem("Customer Accounts", "/admin/ar-recovery/customer-accounts", "Customer-level account workspace.", "Recovery", 1),
      navItem("Aging Management", "/admin/ar-recovery/aging-management", "Aging upload and tracking.", "Recovery", 2),
      navItem("Promise-to-Pay", "/admin/ar-recovery/promise-to-pay", "Promise tracking and follow-up.", "Recovery", 3),
      navItem("Statements", "/admin/ar-recovery/statements", "Statement generation and review.", "Operations", 0),
      navItem("Collection Letters", "/admin/ar-recovery/collection-letters", "Letter workflows and delivery tracking.", "Operations", 1),
      navItem("Reporting", "/admin/ar-recovery/recovery-reporting", "Collector and recovery reporting.", "Insights", 0),
    ],
    defaultFeatureFlags: [
      featureFlag("aging", "Aging management", "Aging uploads, buckets, and prioritization.", true),
      featureFlag("tickler", "Tickler engine", "Follow-up reminders and escalation readiness.", true),
      featureFlag("recovery", "Recovery analytics", "Collector performance and recovery trends.", true),
      featureFlag("white_label", "White-label support", "Tenant branding and portal presentation.", true),
    ],
    defaultRoleKeys: ["admin", "manager", "team_member", "client", "viewer"],
  }),
];

export interface CreateBusinessCenterWorkspaceProvisioningInput {
  id?: string;
  companyId: string;
  companyName: string;
  companySlug: string;
  parentCompanyId?: string | null;
  workspaceKey: BusinessCenterWorkspaceKey;
  workspaceName: string;
  workspaceLabel: string;
  workspaceType: BusinessCenterWorkspaceType;
  brand: string;
  status: BusinessCenterWorkspaceStatus;
  provisioningStatus: BusinessCenterWorkspaceProvisioningStatus;
  portalStatus: BusinessCenterWorkspaceRecord["portalStatus"];
  portalUrl: string;
  ownerUserId?: string | null;
  assignedUserIds: string[];
  assignmentUsers?: BusinessCenterUserRecord[];
  teamAssignment: string;
  modules: string[];
  navigation: BusinessCenterWorkspaceNavigationItem[];
  featureFlags: BusinessCenterWorkspaceFeatureFlag[];
  permissions: {
    roleKeys: BusinessCenterRoleKey[];
    notes: string;
  };
  branding: BusinessCenterWorkspaceRecord["branding"];
  checklist: BusinessCenterWorkspaceRecord["checklist"];
  sourceOnboardingId?: string | null;
  sourceBrandingProfileId?: string | null;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BusinessCenterWorkspaceProvisioningDashboard {
  totalWorkspaces: number;
  activeWorkspaces: number;
  provisioningWorkspaces: number;
  draftWorkspaces: number;
  pausedWorkspaces: number;
  archivedWorkspaces: number;
  portalReadyWorkspaces: number;
  brandingReadyWorkspaces: number;
  userAssignments: number;
  permissionProfiles: number;
  statusCounts: Record<BusinessCenterWorkspaceStatus, number>;
  templateCounts: Record<BusinessCenterWorkspaceKey, number>;
  companyCounts: Record<string, number>;
  recentEvents: BusinessCenterWorkspaceAuditRecord[];
  companyCount: number;
  userCount: number;
}

export function loadBusinessCenterWorkspaceTemplates() {
  return workspaceTemplateCatalog;
}

export async function loadBusinessCenterWorkspaceProvisioningRecords(): Promise<BusinessCenterWorkspaceRecord[]> {
  const cached = readWorkspaceProvisioningCache();

  try {
    const remote = await getBusinessCenterWorkspaces();
    const normalizedRemote = Array.isArray(remote.workspaces) ? remote.workspaces.map((record) => normalizeWorkspaceRecord(record)) : [];
    if (normalizedRemote.length > 0) {
      writeWorkspaceProvisioningCache(normalizedRemote);
      return normalizedRemote;
    }
    if (cached.length > 0) {
      await bulkImportBusinessCenterWorkspaces(cached).catch(() => undefined);
      return cached;
    }
    return cached;
  } catch {
    return cached;
  }
}

export async function saveBusinessCenterWorkspaceProvisioningRecords(records: BusinessCenterWorkspaceRecord[]) {
  const normalized = records.map((record) => normalizeWorkspaceRecord(record));
  writeWorkspaceProvisioningCache(normalized);
  try {
    const remote = await bulkImportBusinessCenterWorkspaces(normalized);
    const remoteRecords = Array.isArray(remote.workspaces) ? remote.workspaces.map((record) => normalizeWorkspaceRecord(record)) : normalized;
    writeWorkspaceProvisioningCache(remoteRecords);
    return remoteRecords;
  } catch {
    return normalized;
  }
}

export async function upsertBusinessCenterWorkspaceProvisioningRecord(nextRecord: BusinessCenterWorkspaceRecord) {
  const current = readWorkspaceProvisioningCache();
  const existing = current.find((record) => record.id === nextRecord.id) || null;
  const now = new Date().toISOString();
  const normalized = normalizeWorkspaceRecord({
    ...nextRecord,
    auditLog: appendWorkspaceAuditLog(existing?.auditLog || [], existing, nextRecord),
    statusHistory: appendWorkspaceStatusHistory(
      existing?.statusHistory || [],
      existing?.id || nextRecord.id,
      existing?.status || null,
      nextRecord.status,
      nextRecord.notes || "",
    ),
    updatedAt: now,
  });
  const exists = current.some((record) => record.id === normalized.id);
  const records = exists ? current.map((record) => (record.id === normalized.id ? normalized : record)) : [normalized, ...current];
  writeWorkspaceProvisioningCache(records);
  try {
    const remote = await upsertBusinessCenterWorkspace(normalized);
    const remoteWorkspace = normalizeWorkspaceRecord(remote.workspace);
    const nextRecords = records.some((record) => record.id === remoteWorkspace.id)
      ? records.map((record) => (record.id === remoteWorkspace.id ? remoteWorkspace : record))
      : [remoteWorkspace, ...records];
    writeWorkspaceProvisioningCache(nextRecords);
    return remoteWorkspace;
  } catch {
    return normalized;
  }
}

export async function updateBusinessCenterWorkspaceProvisioningRecord(
  workspaceId: string,
  changes: Partial<BusinessCenterWorkspaceRecord>,
) {
  const current = readWorkspaceProvisioningCache();
  const records = current.map((record) => {
    if (record.id !== workspaceId) return record;
    return normalizeWorkspaceRecord({
      ...record,
      ...changes,
      auditLog: appendWorkspaceAuditLog(record.auditLog, record, { ...record, ...changes }),
      statusHistory: appendWorkspaceStatusHistory(
        record.statusHistory,
        record.id,
        record.status,
        changes.status || record.status,
        changes.notes || record.notes,
      ),
      updatedAt: new Date().toISOString(),
    });
  });
  writeWorkspaceProvisioningCache(records);
  try {
    const remote = await updateBusinessCenterWorkspace(workspaceId, {
      changes,
    });
    const remoteWorkspace = normalizeWorkspaceRecord(remote.workspace);
    const nextRecords = records.some((record) => record.id === remoteWorkspace.id)
      ? records.map((record) => (record.id === remoteWorkspace.id ? remoteWorkspace : record))
      : [remoteWorkspace, ...records];
    writeWorkspaceProvisioningCache(nextRecords);
    return nextRecords;
  } catch {
    return records;
  }
}

export async function setBusinessCenterWorkspaceStatus(workspaceId: string, status: BusinessCenterWorkspaceStatus, note: string) {
  const current = readWorkspaceProvisioningCache();
  const records = current.map((record) => {
    if (record.id !== workspaceId) return record;
    return normalizeWorkspaceRecord({
      ...record,
      status,
      provisioningStatus: status === "Active" ? "Complete" : status === "Archived" ? "Complete" : record.provisioningStatus,
      portalStatus: status === "Active" ? "active" : status === "Paused" ? "paused" : status === "Archived" ? "paused" : record.portalStatus,
      notes: note || record.notes,
      auditLog: appendWorkspaceAuditLog(record.auditLog, record, { ...record, status, notes: note }),
      statusHistory: appendWorkspaceStatusHistory(record.statusHistory, record.id, record.status, status, note),
      updatedAt: new Date().toISOString(),
    });
  });
  writeWorkspaceProvisioningCache(records);
  try {
    const remote = await updateBusinessCenterWorkspace(workspaceId, {
      status,
      note,
    });
    const remoteWorkspace = normalizeWorkspaceRecord(remote.workspace);
    const nextRecords = records.some((record) => record.id === remoteWorkspace.id)
      ? records.map((record) => (record.id === remoteWorkspace.id ? remoteWorkspace : record))
      : [remoteWorkspace, ...records];
    writeWorkspaceProvisioningCache(nextRecords);
    return nextRecords;
  } catch {
    return records;
  }
}

function readWorkspaceProvisioningCache(): BusinessCenterWorkspaceRecord[] {
  if (workspaceProvisioningCache) return workspaceProvisioningCache.map((record) => normalizeWorkspaceRecord(record));
  workspaceProvisioningCache = createSeedWorkspaceRecords();
  return workspaceProvisioningCache.map((record) => normalizeWorkspaceRecord(record));
}

function writeWorkspaceProvisioningCache(records: BusinessCenterWorkspaceRecord[]) {
  workspaceProvisioningCache = records.map((record) => normalizeWorkspaceRecord(record));
}

export function buildBusinessCenterWorkspaceProvisioningDashboard(
  records: BusinessCenterWorkspaceRecord[],
  companies: BusinessCenterCompanyRecord[] = loadBusinessCenterCompanies(),
  users: BusinessCenterUserRecord[] = loadBusinessCenterCompanyUsers(),
) {
  const statusCounts: Record<BusinessCenterWorkspaceStatus, number> = {
    Draft: 0,
    Provisioning: 0,
    Active: 0,
    Paused: 0,
    Archived: 0,
  };
  const templateCounts: Record<BusinessCenterWorkspaceKey, number> = {
    kreatethedate: 0,
    truevance_connect: 0,
    memory_media_vault: 0,
    ar_recovery: 0,
    custom: 0,
  };
  const companyCounts: Record<string, number> = {};

  let portalReadyWorkspaces = 0;
  let brandingReadyWorkspaces = 0;
  let userAssignments = 0;
  let permissionProfiles = 0;

  records.forEach((record) => {
    statusCounts[record.status] += 1;
    templateCounts[record.workspaceKey] += 1;
    companyCounts[record.companyId] = (companyCounts[record.companyId] || 0) + 1;
    if (record.portalStatus === "active") portalReadyWorkspaces += 1;
    if (Boolean(record.branding.logo.trim()) && Boolean(record.branding.primaryColor.trim()) && Boolean(record.branding.accentColor.trim())) {
      brandingReadyWorkspaces += 1;
    }
    userAssignments += record.assignedUserIds.length;
    permissionProfiles += record.permissions.profiles.length;
  });

  const recentEvents = records
    .flatMap((record) => record.auditLog.map((event) => ({ ...event, workspaceId: record.id })))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, 5)
    .map((event) => ({
      id: event.id,
      workspaceId: event.workspaceId,
      companyId: records.find((record) => record.id === event.workspaceId)?.companyId || "",
      eventType: event.eventType,
      summary: event.summary,
      actorName: event.actorName,
      actorEmail: event.actorEmail,
      createdAt: event.createdAt,
    }));

  return {
    totalWorkspaces: records.length,
    activeWorkspaces: records.filter((record) => record.status === "Active").length,
    provisioningWorkspaces: records.filter((record) => record.status === "Provisioning").length,
    draftWorkspaces: records.filter((record) => record.status === "Draft").length,
    pausedWorkspaces: records.filter((record) => record.status === "Paused").length,
    archivedWorkspaces: records.filter((record) => record.status === "Archived").length,
    portalReadyWorkspaces,
    brandingReadyWorkspaces,
    userAssignments,
    permissionProfiles,
    statusCounts,
    templateCounts,
    companyCounts,
    recentEvents,
    companyCount: companies.length,
    userCount: users.length,
  };
}

function createSeedWorkspaceRecords(): BusinessCenterWorkspaceRecord[] {
  const companies = loadBusinessCenterCompanies();
  const users = loadBusinessCenterCompanyUsers();
  const onboarding = loadBusinessCenterOnboardingRecords();
  const now = new Date().toISOString();

  return companies.map((company) => {
    const onboardingRecord = onboarding.find((record) => record.companyId === company.id) || null;
    const brandingProfile = resolveBusinessCenterBrandingProfile(company.id, company.companySlug, company.companyName, company.brand);
    const template = resolveTemplateForCompany(company);
    const companyUsers = users.filter((user) => user.companyId === company.id);
    const workspaceId = `workspace-${company.companySlug}`;
    const assignments = buildWorkspaceAssignments(company, companyUsers, workspaceId, template.key);
    return normalizeWorkspaceRecord(
      createBusinessCenterWorkspaceRecord({
        id: workspaceId,
        companyId: company.id,
        companyName: company.companyName,
        companySlug: company.companySlug,
        parentCompanyId: company.parentCompanyId || null,
        workspaceKey: template.key,
        workspaceName: buildWorkspaceName(company, template),
        workspaceLabel: template.label,
        workspaceType: template.workspaceType,
        brand: company.brand,
        status: resolveWorkspaceStatus(onboardingRecord),
        provisioningStatus: resolveProvisioningStatus(onboardingRecord),
        portalStatus: resolvePortalStatus(onboardingRecord),
        portalUrl: buildWorkspacePortalUrl(company.companySlug, template.key),
        ownerUserId: companyUsers[0]?.id || null,
        assignedUserIds: assignments.map((assignment) => assignment.userId).filter((value): value is string => Boolean(value)),
        assignmentUsers: companyUsers,
        teamAssignment: buildTeamAssignmentLabel(company, companyUsers),
        modules: template.defaultModules,
        navigation: template.defaultNavigation,
        featureFlags: template.defaultFeatureFlags,
        permissions: {
          roleKeys: template.defaultRoleKeys,
          notes: `Default ${template.label} permission matrix aligned to Business Center OS role definitions.`,
        },
        branding: {
          companyName: brandingProfile?.publicDisplayName || company.companyName,
          logo: brandingProfile?.logoUrl || `${company.companySlug}-logo.svg`,
          primaryColor: brandingProfile?.primaryColor || template.primaryColor,
          accentColor: brandingProfile?.accentColor || template.accentColor,
          contactEmail: brandingProfile?.contactEmail || `hello@${company.companySlug}.com`,
          contactPhone: brandingProfile?.phoneNumber || "",
          domain: brandingProfile?.domains.primaryDomain || `${company.companySlug}.com`,
          portalDomain: brandingProfile?.domains.portalDomain || `portal.${company.companySlug}.com`,
        },
        checklist: {
          companyLinked: true,
          brandingMapped: Boolean(brandingProfile?.companyName),
          portalActivated: resolvePortalStatus(onboardingRecord) === "active",
          navigationLoaded: true,
          permissionsMapped: true,
          usersAssigned: assignments.length > 0,
          reportingEnabled: true,
        },
        sourceOnboardingId: onboardingRecord?.id || null,
        sourceBrandingProfileId: brandingProfile?.id || null,
        notes:
          onboardingRecord?.notes ||
          `Provisioned for ${company.companyName}. This workspace is ready for company-scoped access, navigation, and future automation.`,
        createdAt: now,
        updatedAt: now,
      }),
    );
  });
}

export function createBusinessCenterWorkspaceRecord(input: CreateBusinessCenterWorkspaceProvisioningInput): BusinessCenterWorkspaceRecord {
  const now = input.createdAt || new Date().toISOString();
  const workspaceId = input.id || `workspace-${input.companySlug}-${input.workspaceKey}`;
  const permissionsProfiles = buildPermissionProfiles(input.companyId, workspaceId, input.permissions.roleKeys, input.permissions.notes);

  return {
    id: workspaceId,
    companyId: input.companyId,
    parentCompanyId: input.parentCompanyId || null,
    companyName: input.companyName || "Untitled Company",
    companySlug: input.companySlug || slugify(input.companyName || "untitled-company"),
    workspaceKey: input.workspaceKey,
    workspaceName: input.workspaceName || input.workspaceLabel || "Untitled Workspace",
    workspaceLabel: input.workspaceLabel || input.workspaceName || "Workspace",
    workspaceType: input.workspaceType,
    brand: input.brand || "truevance_connect",
    status: input.status,
    provisioningStatus: input.provisioningStatus,
    portalStatus: input.portalStatus,
    portalUrl: input.portalUrl,
    ownerUserId: input.ownerUserId || null,
    assignedUserIds: ensureUniqueIds(input.assignedUserIds),
    teamAssignment: input.teamAssignment || "",
    modules: ensureUniqueStrings(input.modules),
    navigation: normalizeNavigation(input.navigation),
    featureFlags: normalizeFeatureFlags(input.featureFlags),
    assignments: ensureUniqueIds(input.assignedUserIds).map((userId) =>
      buildWorkspaceAssignmentFromUser(
        userId,
        input.companyId,
        workspaceId,
        input.workspaceKey,
        input.status,
        input.assignmentUsers?.find((candidate) => candidate.id === userId),
      ),
    ),
    permissions: {
      roleKeys: ensureUniqueRoleKeys(input.permissions.roleKeys),
      profiles: permissionsProfiles,
      notes: input.permissions.notes,
    },
    branding: {
      companyName: input.branding.companyName || input.companyName,
      logo: input.branding.logo || "",
      primaryColor: input.branding.primaryColor || "#1c69d4",
      accentColor: input.branding.accentColor || "#14b8a6",
      contactEmail: input.branding.contactEmail || "hello@example.com",
      contactPhone: input.branding.contactPhone || "",
      domain: input.branding.domain || `${input.companySlug}.com`,
      portalDomain: input.branding.portalDomain || `portal.${input.companySlug}.com`,
    },
    checklist: {
      companyLinked: Boolean(input.checklist.companyLinked),
      brandingMapped: Boolean(input.checklist.brandingMapped),
      portalActivated: Boolean(input.checklist.portalActivated),
      navigationLoaded: Boolean(input.checklist.navigationLoaded),
      permissionsMapped: Boolean(input.checklist.permissionsMapped),
      usersAssigned: Boolean(input.checklist.usersAssigned),
      reportingEnabled: Boolean(input.checklist.reportingEnabled),
    },
    statusHistory: [],
    auditLog: [],
    sourceOnboardingId: input.sourceOnboardingId || null,
    sourceBrandingProfileId: input.sourceBrandingProfileId || null,
    notes: input.notes || "",
    createdAt: now,
    updatedAt: input.updatedAt || now,
  };
}

function buildWorkspaceTemplate(input: Omit<BusinessCenterWorkspaceTemplateRecord, "id" | "createdAt" | "updatedAt">): BusinessCenterWorkspaceTemplateRecord {
  const now = new Date().toISOString();
  return {
    id: `workspace-template-${input.key}`,
    ...input,
    defaultNavigation: normalizeNavigation(input.defaultNavigation),
    defaultFeatureFlags: normalizeFeatureFlags(input.defaultFeatureFlags),
    defaultRoleKeys: ensureUniqueRoleKeys(input.defaultRoleKeys),
    createdAt: now,
    updatedAt: now,
  };
}

function resolveTemplateForCompany(company: BusinessCenterCompanyRecord): BusinessCenterWorkspaceTemplateRecord {
  const templateKey = resolveTemplateKey(company);
  return workspaceTemplateCatalog.find((template) => template.key === templateKey) || workspaceTemplateCatalog[0];
}

function resolveTemplateKey(company: BusinessCenterCompanyRecord): BusinessCenterWorkspaceKey {
  if (company.companyType === "parent") return "custom";
  if (company.brand === "kreatethedate") return "kreatethedate";
  if (company.brand === "truevance_connect") return "truevance_connect";
  if (company.brand === "memory_media_vault") return "memory_media_vault";
  if (company.brand === "ar_recovery") return "ar_recovery";
  return "custom";
}

function resolveWorkspaceStatus(onboarding: BusinessCenterOnboardingRecord | null): BusinessCenterWorkspaceStatus {
  if (!onboarding) return "Draft";
  if (onboarding.status === "Complete") return "Active";
  if (onboarding.status === "Pending") return "Provisioning";
  if (onboarding.status === "In Progress") return "Provisioning";
  return "Draft";
}

function resolveProvisioningStatus(onboarding: BusinessCenterOnboardingRecord | null): BusinessCenterWorkspaceProvisioningStatus {
  if (!onboarding) return "Not Started";
  if (onboarding.status === "Complete") return "Complete";
  if (onboarding.status === "Pending") return "Pending";
  if (onboarding.status === "In Progress") return "In Progress";
  return "Not Started";
}

function resolvePortalStatus(onboarding: BusinessCenterOnboardingRecord | null): BusinessCenterWorkspaceRecord["portalStatus"] {
  if (!onboarding) return "draft";
  return onboarding.portalStatus;
}

function buildWorkspaceName(company: BusinessCenterCompanyRecord, template: BusinessCenterWorkspaceTemplateRecord) {
  if (company.companyType === "parent") return "Business Center Core Workspace";
  if (template.key === "custom") return `${company.companyName} Workspace`;
  return `${template.label}`;
}

function buildTeamAssignmentLabel(company: BusinessCenterCompanyRecord, users: BusinessCenterUserRecord[]) {
  if (users.some((user) => mapCompanyRoleToPermissionRole(user.role) === "admin")) return "Leadership";
  if (users.some((user) => mapCompanyRoleToPermissionRole(user.role) === "manager")) return "Operations";
  if (company.brand === "ar_recovery") return "Recovery Operations";
  if (company.brand === "kreatethedate") return "Client Experience";
  return "Delivery";
}

function buildWorkspaceAssignments(
  company: BusinessCenterCompanyRecord,
  companyUsers: BusinessCenterUserRecord[],
  workspaceId: string,
  workspaceKey: BusinessCenterWorkspaceKey,
): BusinessCenterWorkspaceAssignmentRecord[] {
  return companyUsers.map((user) => buildWorkspaceAssignmentFromUser(user.id, company.id, workspaceId, workspaceKey, "Active", user));
}

function buildWorkspaceAssignmentFromUser(
  userId: string,
  companyId: string,
  workspaceId: string,
  workspaceKey: BusinessCenterWorkspaceKey,
  status: BusinessCenterWorkspaceStatus,
  user?: BusinessCenterUserRecord,
): BusinessCenterWorkspaceAssignmentRecord {
  const roleKey = mapCompanyRoleToPermissionRole(user?.role || "viewer");
  const assignmentType =
    roleKey === "admin"
      ? "owner"
      : roleKey === "manager"
        ? "manager"
        : roleKey === "team_member"
          ? "team"
          : roleKey === "client"
            ? "client"
            : "viewer";
  const statusValue =
    user?.status === "active"
      ? "active"
      : user?.status === "deactivated"
        ? "suspended"
        : user?.status === "archived"
          ? "revoked"
          : status === "Archived"
            ? "revoked"
            : status === "Paused"
              ? "suspended"
              : "invited";
  return {
    id: `workspace-assignment-${workspaceKey}-${userId}`,
    companyId,
    workspaceId,
    userId,
    roleKey,
    assignmentType,
    status: statusValue,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function buildPermissionProfiles(
  companyId: string,
  workspaceId: string,
  roleKeys: BusinessCenterRoleKey[],
  notes: string,
): BusinessCenterWorkspacePermissionProfileRecord[] {
  return ensureUniqueRoleKeys(roleKeys).map((roleKey) => ({
    id: `workspace-permission-${workspaceId}-${roleKey}`,
    companyId,
    workspaceId,
    roleKey,
    matrix: businessCenterPermissionMatrixDefaults,
    notes,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));
}

function appendWorkspaceStatusHistory(
  history: BusinessCenterWorkspaceStatusHistoryRecord[],
  workspaceId: string,
  fromStatus: BusinessCenterWorkspaceStatus | null | undefined,
  toStatus: BusinessCenterWorkspaceStatus,
  note: string,
): BusinessCenterWorkspaceStatusHistoryRecord[] {
  if (!toStatus || fromStatus === toStatus) return history;
  return [
    ...history,
    {
      id: `workspace-status-${crypto.randomUUID()}`,
      workspaceId,
      fromStatus: fromStatus || null,
      toStatus,
      note,
      createdAt: new Date().toISOString(),
    },
  ];
}

function appendWorkspaceAuditLog(
  history: BusinessCenterWorkspaceAuditRecord[],
  existing: BusinessCenterWorkspaceRecord | null,
  next: Partial<BusinessCenterWorkspaceRecord>,
): BusinessCenterWorkspaceAuditRecord[] {
  const events = [...history];
  const current = existing;
  const baseName = next.workspaceLabel || next.workspaceName || current?.workspaceLabel || current?.workspaceName || "Workspace";
  const now = new Date().toISOString();
  if (!current) {
    events.push({
      id: `workspace-audit-${crypto.randomUUID()}`,
      workspaceId: next.id || "",
      companyId: next.companyId || "",
      eventType: "Created",
      summary: `${baseName} created for ${next.companyName || "a company"}.`,
      createdAt: now,
    });
    return events;
  }
  if (next.status && next.status !== current.status) {
    events.push({
      id: `workspace-audit-${crypto.randomUUID()}`,
      workspaceId: current.id,
      companyId: current.companyId,
      eventType: next.status === "Active" ? "Activated" : next.status === "Paused" ? "Paused" : next.status === "Archived" ? "Archived" : "Updated",
      summary: `${baseName} status changed from ${current.status} to ${next.status}.`,
      createdAt: now,
    });
  }
  if (next.branding && JSON.stringify(next.branding) !== JSON.stringify(current.branding)) {
    events.push({
      id: `workspace-audit-${crypto.randomUUID()}`,
      workspaceId: current.id,
      companyId: current.companyId,
      eventType: "Branding Updated",
      summary: `${baseName} branding profile updated.`,
      createdAt: now,
    });
  }
  if (next.navigation && JSON.stringify(next.navigation) !== JSON.stringify(current.navigation)) {
    events.push({
      id: `workspace-audit-${crypto.randomUUID()}`,
      workspaceId: current.id,
      companyId: current.companyId,
      eventType: "Navigation Updated",
      summary: `${baseName} navigation blueprint updated.`,
      createdAt: now,
    });
  }
  if (next.permissions && JSON.stringify(next.permissions) !== JSON.stringify(current.permissions)) {
    events.push({
      id: `workspace-audit-${crypto.randomUUID()}`,
      workspaceId: current.id,
      companyId: current.companyId,
      eventType: "Permission Updated",
      summary: `${baseName} permission mapping updated.`,
      createdAt: now,
    });
  }
  if (Array.isArray(next.assignedUserIds) && JSON.stringify(next.assignedUserIds) !== JSON.stringify(current.assignedUserIds)) {
    events.push({
      id: `workspace-audit-${crypto.randomUUID()}`,
      workspaceId: current.id,
      companyId: current.companyId,
      eventType: "Assignment Updated",
      summary: `${baseName} assignment list updated.`,
      createdAt: now,
    });
  }
  return events;
}

function normalizeWorkspaceRecord(record: BusinessCenterWorkspaceRecord): BusinessCenterWorkspaceRecord {
  return {
    ...record,
    id: record.id || `workspace-${record.companySlug || slugify(record.companyName || "workspace")}`,
    companyId: record.companyId,
    parentCompanyId: record.parentCompanyId || null,
    companyName: record.companyName || "Untitled Company",
    companySlug: record.companySlug || slugify(record.companyName || "untitled-company"),
    workspaceKey: record.workspaceKey || "custom",
    workspaceName: record.workspaceName || record.workspaceLabel || "Untitled Workspace",
    workspaceLabel: record.workspaceLabel || record.workspaceName || "Workspace",
    workspaceType: record.workspaceType || "custom",
    brand: record.brand || "truevance_connect",
    status: workspaceStatusOptions.includes(record.status) ? record.status : "Draft",
    provisioningStatus: workspaceProvisioningStatusOptions.includes(record.provisioningStatus) ? record.provisioningStatus : "Not Started",
    portalStatus:
      record.portalStatus === "draft" || record.portalStatus === "pending" || record.portalStatus === "active" || record.portalStatus === "paused"
        ? record.portalStatus
        : "draft",
    portalUrl: record.portalUrl || buildWorkspacePortalUrl(record.companySlug || record.companyName, record.workspaceKey || "custom"),
    ownerUserId: record.ownerUserId || null,
    assignedUserIds: ensureUniqueIds(record.assignedUserIds),
    teamAssignment: record.teamAssignment || "",
    modules: ensureUniqueStrings(record.modules),
    navigation: normalizeNavigation(record.navigation),
    featureFlags: normalizeFeatureFlags(record.featureFlags),
    assignments: Array.isArray(record.assignments)
      ? record.assignments.map((assignment) => normalizeAssignment({ ...assignment, workspaceId: record.id }))
      : [],
    permissions: {
      roleKeys: ensureUniqueRoleKeys(record.permissions?.roleKeys || []),
      profiles: Array.isArray(record.permissions?.profiles)
        ? record.permissions.profiles.map((profile) => normalizePermissionProfile({ ...profile, workspaceId: record.id }))
        : [],
      notes: record.permissions?.notes || "",
    },
    branding: {
      companyName: record.branding?.companyName || record.companyName || "Untitled Company",
      logo: record.branding?.logo || "",
      primaryColor: record.branding?.primaryColor || "#1c69d4",
      accentColor: record.branding?.accentColor || "#14b8a6",
      contactEmail: record.branding?.contactEmail || "hello@example.com",
      contactPhone: record.branding?.contactPhone || "",
      domain: record.branding?.domain || "",
      portalDomain: record.branding?.portalDomain || "",
    },
    checklist: {
      companyLinked: Boolean(record.checklist?.companyLinked),
      brandingMapped: Boolean(record.checklist?.brandingMapped),
      portalActivated: Boolean(record.checklist?.portalActivated),
      navigationLoaded: Boolean(record.checklist?.navigationLoaded),
      permissionsMapped: Boolean(record.checklist?.permissionsMapped),
      usersAssigned: Boolean(record.checklist?.usersAssigned),
      reportingEnabled: Boolean(record.checklist?.reportingEnabled),
    },
    statusHistory: Array.isArray(record.statusHistory)
      ? record.statusHistory.map((item) => normalizeStatusHistory({ ...item, workspaceId: record.id }))
      : [],
    auditLog: Array.isArray(record.auditLog)
      ? record.auditLog.map((item) => normalizeAuditRecord({ ...item, workspaceId: record.id, companyId: record.companyId }))
      : [],
    sourceOnboardingId: record.sourceOnboardingId || null,
    sourceBrandingProfileId: record.sourceBrandingProfileId || null,
    notes: record.notes || "",
    createdAt: record.createdAt || new Date().toISOString(),
    updatedAt: record.updatedAt || new Date().toISOString(),
  };
}

function normalizeAssignment(assignment: BusinessCenterWorkspaceAssignmentRecord): BusinessCenterWorkspaceAssignmentRecord {
  const roleKeys: BusinessCenterRoleKey[] = ["admin", "manager", "team_member", "client", "viewer"];
  const assignmentTypes: BusinessCenterWorkspaceAssignmentRecord["assignmentType"][] = ["owner", "admin", "manager", "team", "client", "viewer"];
  const statuses: BusinessCenterWorkspaceAssignmentRecord["status"][] = ["invited", "active", "suspended", "revoked"];
  return {
    ...assignment,
    id: assignment.id || `workspace-assignment-${assignment.workspaceId}-${assignment.userId || "unassigned"}`,
    roleKey: roleKeys.includes(assignment.roleKey) ? assignment.roleKey : "viewer",
    assignmentType: assignmentTypes.includes(assignment.assignmentType) ? assignment.assignmentType : "viewer",
    status: statuses.includes(assignment.status) ? assignment.status : "invited",
    createdAt: assignment.createdAt || new Date().toISOString(),
    updatedAt: assignment.updatedAt || new Date().toISOString(),
  };
}

function normalizePermissionProfile(profile: BusinessCenterWorkspacePermissionProfileRecord): BusinessCenterWorkspacePermissionProfileRecord {
  return {
    ...profile,
    id: profile.id || `workspace-permission-${profile.workspaceId}-${profile.roleKey}`,
    roleKey: ["admin", "manager", "team_member", "client", "viewer"].includes(profile.roleKey) ? profile.roleKey : "viewer",
    notes: profile.notes || "",
    createdAt: profile.createdAt || new Date().toISOString(),
    updatedAt: profile.updatedAt || new Date().toISOString(),
  };
}

function normalizeStatusHistory(item: BusinessCenterWorkspaceStatusHistoryRecord): BusinessCenterWorkspaceStatusHistoryRecord {
  return {
    ...item,
    id: item.id || `workspace-status-${item.workspaceId}-${item.toStatus}`,
    fromStatus: item.fromStatus || null,
    toStatus: workspaceStatusOptions.includes(item.toStatus) ? item.toStatus : "Draft",
    note: item.note || "",
    createdAt: item.createdAt || new Date().toISOString(),
  };
}

function normalizeAuditRecord(record: BusinessCenterWorkspaceAuditRecord): BusinessCenterWorkspaceAuditRecord {
  return {
    ...record,
    id: record.id || `workspace-audit-${record.workspaceId}-${record.eventType}`,
    eventType: record.eventType || "Updated",
    summary: record.summary || "",
    actorName: record.actorName || null,
    actorEmail: record.actorEmail || null,
    createdAt: record.createdAt || new Date().toISOString(),
  };
}

function normalizeNavigation(items: BusinessCenterWorkspaceNavigationItem[]) {
  return ensureUniqueNavigation(Array.isArray(items) ? items : []);
}

function ensureUniqueNavigation(items: BusinessCenterWorkspaceNavigationItem[]) {
  return items
    .map((item, index) => ({
      label: item.label || "Untitled",
      path: item.path || "/admin/truevance-business-center",
      description: item.description || "",
      group: item.group || "General",
      enabled: item.enabled !== false,
      sortOrder: Number.isFinite(item.sortOrder) ? item.sortOrder : index,
    }))
    .sort((left, right) => left.sortOrder - right.sortOrder);
}

function normalizeFeatureFlags(flags: BusinessCenterWorkspaceFeatureFlag[]) {
  return ensureUniqueFeatureFlags(Array.isArray(flags) ? flags : []);
}

function ensureUniqueFeatureFlags(flags: BusinessCenterWorkspaceFeatureFlag[]) {
  const seen = new Set<string>();
  return flags
    .map((flag) => ({
      key: flag.key || "feature",
      label: flag.label || "Feature",
      description: flag.description || "",
      enabled: flag.enabled !== false,
    }))
    .filter((flag) => {
      if (seen.has(flag.key)) return false;
      seen.add(flag.key);
      return true;
    });
}

function ensureUniqueIds(values: string[]) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((value) => value.trim()).filter(Boolean)));
}

function ensureUniqueStrings(values: string[]) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((value) => value.trim()).filter(Boolean)));
}

function ensureUniqueRoleKeys(roleKeys: BusinessCenterRoleKey[]) {
  const allowed: BusinessCenterRoleKey[] = ["admin", "manager", "team_member", "client", "viewer"];
  return Array.from(new Set((Array.isArray(roleKeys) ? roleKeys : []).filter((role): role is BusinessCenterRoleKey => allowed.includes(role))));
}

function navItem(label: string, path: string, description: string, group: string, sortOrder: number): BusinessCenterWorkspaceNavigationItem {
  return { label, path, description, group, enabled: true, sortOrder };
}

function featureFlag(key: string, label: string, description: string, enabled: boolean): BusinessCenterWorkspaceFeatureFlag {
  return { key, label, description, enabled };
}

function buildWorkspacePortalUrl(companySlug: string, workspaceKey: BusinessCenterWorkspaceKey) {
  const params = new URLSearchParams({
    company: slugify(companySlug),
    workspace: workspaceKey,
  });
  return `/client/portal?${params.toString()}`;
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-") || "workspace"
  );
}
